import React, { useState, useEffect, useCallback } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import { propTypes } from '../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
  ExternalLink,
  LanguageMenu,
  Modal,
} from '../../../components';
import MenuIcon from '../../MenuIcon/MenuIcon';
import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import additionalMenuItems from '../additionalMenuItems';
import {
  getMarketplaceId,
  isMarketplaceRespare,
  isMarketplaceSecondLife,
  SEARCH_PAGE_TYPE,
} from '../../../util/marketplace';
import {
  isUserVerifiedDashboardViewer,
  isUserVerifiedAdmin,
  isUserVerifiedCompanyDealer,
} from '../../../util/user';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import QRCodeComponent from '../../QRCode/QRCodeComponent';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import { useDispatch } from 'react-redux';
import { isHomePath } from '../../../util/uiHelpers';

import css from './TopbarDesktop.module.css';

const TopbarDesktop = props => {
  const {
    className,
    appConfig,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    company,
    queryCompanyInProgress,
  } = props;
  const [mounted, setMounted] = useState(false);

  const history = useHistory();
  const currentPath = history?.location?.pathname;

  const dispatch = useDispatch();

  useEffect(() => {
    setMounted(true);
  }, []);

  const isWindowDefined = typeof window !== 'undefined';

  const marketplaceName = appConfig.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const classes = classNames(rootClassName || css.root, className);

  const [showQrModal, setShowQrModal] = useState(false);
  const openQrModal = () => setShowQrModal(true);
  const closeQrModal = () => setShowQrModal(false);

  const search = (
    <TopbarSearchForm
      className={classNames(css.searchLink, {
        [css.searchLinkOnly]: company,
      })}
      desktopInputRoot={css.topbarSearchWithLeftPadding}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={appConfig}
      isAuthenticated={isAuthenticated}
    />
  );

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const inboxLink = authenticatedOnClientSide ? (
    <NamedLink
      className={css.inboxLink}
      name="InboxPage"
      params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {notificationDot}
      </span>
    </NamedLink>
  ) : null;

  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  const onManageDisableScrolling = useCallback(
    (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    [dispatch]
  );

  // Only for 2ndLife: we need to keep productType query paramter in the url to differ views for Husqvarna Construction and Husqvarna Robotic Lawnmowers
  const searchString = history?.location?.search || '';
  const params = new URLSearchParams(searchString);
  const productType = params.get('pub_productType');
  const searchParams =
    isMarketplaceSecondLife(appConfig) && productType ? `?pub_productType=${productType}` : '';

  const profileMenu =
    authenticatedOnClientSide && !company ? (
      <Menu>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        </MenuLabel>

        <MenuContent className={css.profileMenuContent}>
          {isMarketplaceRespare(appConfig) && (
            <MenuItem key="ManageListingsPage">
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            </MenuItem>
          )}
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(
                css.profileSettingsLink,
                currentPageClass('ProfileSettingsPage')
              )}
              name="ProfileSettingsPage"
              to={{ search: searchParams }}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          {isMarketplaceRespare(appConfig) && (
            <MenuItem key="AccountSettingsPage">
              <NamedLink
                className={classNames(
                  css.yourListingsLink,
                  currentPageClass('AccountSettingsPage')
                )}
                name="AccountSettingsPage"
                to={{ search: searchParams }}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
              </NamedLink>
            </MenuItem>
          )}
          {isMarketplaceSecondLife(appConfig) &&
            (isUserVerifiedAdmin(currentUser) || isUserVerifiedDashboardViewer(currentUser)) && (
              <>
                <MenuItem key="MembersAdminPage">
                  <NamedLink
                    className={classNames(
                      css.yourListingsLink,
                      currentPageClass('MembersAdminPage')
                    )}
                    name="MembersAdminPage"
                    to={{ search: searchParams }}
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="General.users" />
                  </NamedLink>
                </MenuItem>
                <MenuItem key="DealersAdminPage">
                  <NamedLink
                    className={classNames(
                      css.yourListingsLink,
                      currentPageClass('DealersAdminPage')
                    )}
                    name="DealersAdminPage"
                    to={{ search: searchParams }}
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="General.dealers" />
                  </NamedLink>
                </MenuItem>
                <MenuItem key="CompaniesPage">
                  <NamedLink
                    className={classNames(css.yourListingsLink, currentPageClass('CompaniesPage'))}
                    name="CompaniesPage"
                    to={{ search: searchParams }}
                  >
                    <span className={css.menuItemBorder} />
                    <FormattedMessage id="General.companies" />
                  </NamedLink>
                </MenuItem>
              </>
            )}

          {isMarketplaceSecondLife(appConfig) && isUserVerifiedCompanyDealer(currentUser) && (
            <MenuItem key="MembersPage">
              <NamedLink
                className={classNames(css.yourListingsLink, currentPageClass('MembersPage'))}
                name="MembersPage"
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id="General.companySettings" />
              </NamedLink>
            </MenuItem>
          )}

          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <span className={css.menuItemBorder} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    ) : null;

  const signupLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="SignupPage" className={css.signupLink}>
      <span className={css.signup}>
        <FormattedMessage id="TopbarDesktop.signup" />
      </span>
    </NamedLink>
  );

  const loginLink = isAuthenticatedOrJustHydrated ? null : (
    <NamedLink name="LoginPage" className={css.loginLink}>
      <span className={css.login}>
        <FormattedMessage id="TopbarDesktop.login" />
      </span>
    </NamedLink>
  );

  const burgerMenu = !company && (
    <Menu className={css.additionalLinksMenu}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <MenuIcon />
      </MenuLabel>
      <MenuContent>
        {additionalMenuItems.map(({ translationId, pageName, params, href, marketplaceIds }) => {
          const linkProps = href ? { href } : { name: pageName, params };

          const Component = href ? ExternalLink : NamedLink;

          const page = href || pageName;

          if (!marketplaceIds.includes(getMarketplaceId(appConfig))) return null;

          return (
            <MenuItem key={page}>
              <Component
                className={classNames(
                  css.yourListingsLink,
                  css.additionalLink,
                  currentPageClass(page)
                )}
                to={{ search: searchParams }}
                {...linkProps}
              >
                <span className={css.menuItemBorder} />
                <FormattedMessage id={translationId} />
              </Component>
            </MenuItem>
          );
        })}

        {isMarketplaceSecondLife(appConfig) && isWindowDefined && (
          <MenuItem key="handover">
            <button
              className={classNames(css.yourListingsLink, css.additionalLink)}
              onClick={openQrModal}
            >
              <span className={css.menuItemBorder} />
              <FormattedMessage id="General.handover" />
            </button>
          </MenuItem>
        )}
      </MenuContent>
    </Menu>
  );

  const qrCodeModal = showQrModal && (
    <Modal
      id="QRCodeModal"
      isOpen={showQrModal}
      onClose={closeQrModal}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <QRCodeComponent
        className={css.qrModalContent}
        url={isWindowDefined ? window.location.href : ''}
      />
    </Modal>
  );

  const languagePicker = (
    <LanguageMenu
      className={css.languageMenu}
      searchParams={isMarketplaceSecondLife(appConfig) && searchParams}
    />
  );

  const importListingsButton = isAuthenticated && isMarketplaceSecondLife(appConfig) && !company && (
    <NamedLink
      className={css.createListingLink}
      name="LeaseImportPage"
      to={{ search: searchParams }}
    >
      <span className={css.createListing}>
        <FormattedMessage id="LeaseImportPage.title" />
      </span>
    </NamedLink>
  );

  const isLogoActive =
    isHomePath(currentPath) ||
    (isAuthenticated && currentPath?.includes(SEARCH_PAGE_TYPE.MY_LISTINGS));

  return (
    <nav className={classes}>
      <LinkedLogo
        className={classNames(css.logoLink, isLogoActive && css.activeLogoLink)}
        format="desktop"
        alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
        company={company}
        queryCompanyInProgress={queryCompanyInProgress}
        {...(isAuthenticated && isMarketplaceSecondLife(appConfig)
          ? { name: 'SearchPageMyListings' }
          : {})}
        to={{ search: searchParams }}
      />

      {isAuthenticated && isMarketplaceSecondLife(appConfig) && !company && (
        <>
          <NamedLink
            className={classNames(
              css.createListingLink,
              currentPath?.includes(SEARCH_PAGE_TYPE.MY_LISTINGS) && css.activeListingLink
            )}
            name="SearchPageMyListings"
            to={{ search: searchParams }}
          >
            <span className={css.createListing}>
              <FormattedMessage id="General.myListings" />
            </span>
          </NamedLink>

          <NamedLink
            className={classNames(
              css.createListingLink,
              currentPath?.includes(SEARCH_PAGE_TYPE.SELL_AS_IS) && css.activeListingLink
            )}
            name="SearchPageSellAsIs"
            to={{ search: searchParams }}
          >
            <span className={css.createListing}>
              <FormattedMessage id="General.marketplace" />
            </span>
          </NamedLink>
        </>
      )}

      {search}

      {burgerMenu}

      {qrCodeModal}

      {languagePicker}

      {isAuthenticated && isMarketplaceRespare(appConfig) && (
        <>
          <NamedLink className={css.createListingLink} name="NewListingPage">
            <span className={css.createListing}>
              <FormattedMessage id="TopbarDesktop.createListing" />
            </span>
          </NamedLink>
          <NamedLink className={css.createListingLink} name="NewListingBulkUploadPage">
            <span className={css.createListing}>
              <FormattedMessage id="General.postMultipleListings" />
            </span>
          </NamedLink>
        </>
      )}

      {importListingsButton}

      {isMarketplaceRespare(appConfig) && inboxLink}
      {profileMenu}
      {signupLink}
      {loginLink}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  appConfig: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  appConfig: object,
};

export default TopbarDesktop;
